import { Component } from '@theme/component';
import { debounce, fetchConfig, isDesktopBreakpoint, onIdleAfterPageLoad } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, DialogOpenEvent, ThemeEvents } from '@theme/events';
import { announceCartError, announceCartLinesUpdate, settleWithFetchedCart } from '@theme/standard-cart-events';
import { normalizeSectionId, PrefetchCache } from '@theme/section-renderer';

class ProductFinderComponent extends Component {
  /** Theme section id used for step HTML fetch. */
  static STEP_SECTION_ID = 'section-rendering-product-finder-step';
  /** Cached once; appended as `section_id` on every section fetch URL. */
  static NORMALIZED_STEP_SECTION_ID = normalizeSectionId(ProductFinderComponent.STEP_SECTION_ID);
  /** Max section HTML entries to keep */
  static MAX_CACHE_SIZE = 10;
  /** Delay before a hover prefetch fires (avoids fetching on accidental hover). */
  static HOVER_DELAY = 150;

  /** @type {PrefetchCache<Document>} */
  #cache = new PrefetchCache({
    fetcher: (url, signal) => this.#fetchSection(url, signal),
    maxSize: ProductFinderComponent.MAX_CACHE_SIZE,
    shouldCache: () => !Shopify.designMode,
  });
  /** @type {boolean} */
  #isClickFetching = false;
  /** @type {number | null} */
  #hoverTimeout = null;

  /**
   * Steps in order: `activeIndex` at click time; optional `paramName` when the control has `data-name`.
   * Cleared in design mode cache refresh (and when choosing a collection).
   * @type {{ activeIndex: number, paramName?: string }[]}
   */
  #dataFilterSelections = [];

  /**
   * Readable answers kept in sync with `#dataFilterSelections` (push/pop at the same points).
   * Only populated when email capture is enabled. `null` entries mark skipped steps.
   * @type {({ question: string, answer: string } | null)[]}
   */
  #answerLog = [];

  /** @type {ReturnType<typeof debounce<() => void>> | undefined} */
  #debouncedProgressBarResize;

  /** Original `data-base-url` value — restored on retake. */
  #initialBaseUrl = '';

  connectedCallback() {
    super.connectedCallback();
    this.#initialBaseUrl = this.dataset.baseUrl ?? '';
    document.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.#debouncedProgressBarResize = debounce(() => {
      this.getProgressBarHeight();
    }, 150);
    window.addEventListener('resize', this.#debouncedProgressBarResize);
    onIdleAfterPageLoad(() => {
      this.#observeForAutoLoad();
      // Reopen after idle so the dialog component is ready before we click it open.
      if (this.#emailCaptureEnabled) this.#maybeReopenAfterSubscribe();
    });

    if (this.#emailCaptureEnabled) {
      // Fill the note + stash the result URL right before the native form submit reloads the page.
      this.addEventListener('submit', this.#onSubscribeSubmit);
    }

    // In the theme editor a granular block re-render (e.g. editing a step's Filter
    // name) re-selects the block without recreating this component, so the one-shot
    // auto-load never re-runs. Re-fetch filter content on block select instead.
    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.#onDesignBlockSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    if (this.#debouncedProgressBarResize) {
      window.removeEventListener('resize', this.#debouncedProgressBarResize);
      this.#debouncedProgressBarResize.cancel?.();
      this.#debouncedProgressBarResize = undefined;
    }
    this.#clearHoverTimeout();
    this.removeEventListener('submit', this.#onSubscribeSubmit);
    document.removeEventListener('shopify:block:select', this.#onDesignBlockSelect);
    this.#cache.abort();
  }

  /**
   * Theme editor: re-fetch filter content after a step block is (re)selected.
   * Scoped to steps inside this finder; the populate call no-ops when a
   * collection step exists.
   * @param {Event} event
   */
  #onDesignBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Node) || !this.contains(target)) return;
    this.#populateAutoLoadFilters();
  };

  /** Fired by `dialog-component` as `dialog:open` after the dialog is shown (`dialog.js`). */
  #onDialogOpen = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof Node) || !this.contains(dialog)) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.getProgressBarHeight());
    });
  };

  /**
   * If no collection step exists, observe visibility then fetch + populate all filter slides.
   */
  #observeForAutoLoad() {
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      this.#autoLoadDefaultCollection();
    }, { threshold: 0.1 });
    observer.observe(this);
  }

  /** Clone the dialog template into the DOM (no-op once stamped). */
  #stampDialogContent() {
    const dialogInner = this.querySelector('[ref="dialogInner"]');
    const template = dialogInner?.querySelector(':scope > template[ref="dialogTemplate"]');
    if (!dialogInner || !template) return;
    dialogInner.innerHTML = '';
    dialogInner.appendChild(template.content.cloneNode(true));
  }

  async #autoLoadDefaultCollection() {
    if (this._autoLoaded) return;
    this._autoLoaded = true;
    // Stamp now so swiper initialises before the fetch resolves.
    this.#stampDialogContent();
    await this.#populateAutoLoadFilters();
  }

  /**
   * Fetch the default (all-products) step section and inject the matching filter
   * fragment into every filter step. No-op when a collection step exists.
   */
  async #populateAutoLoadFilters() {
    if (this.querySelector('.product-finder-step--collection')) return;

    const fetchUrl = this.#toFetchUrl(new URL(this.dataset.baseUrl, window.location.origin));
    const newHTML = await this.#cache.get(fetchUrl);
    if (!newHTML) return;

    for (const filterEl of this.querySelectorAll('.product-finder-step__filter[data-filter]')) {
      const key = filterEl.getAttribute('data-filter');
      if (!key) continue;
      const matched = this.#findFilterFragmentForKey(newHTML, key);
      filterEl.innerHTML = matched?.innerHTML ?? '';
    }

    this.#refreshCarouselAutoHeight();
  }

  /**
   * Recompute Swiper auto-height after async filter content is injected.
   * Only touches the wrapper height (updateAutoHeight) — calling the full
   * swiper.update() here re-runs the fade effect and can blank the active slide.
   */
  #refreshCarouselAutoHeight() {
    const swiper = this.querySelector('carousel-component')?.swiper;
    if (!swiper || typeof swiper.updateAutoHeight !== 'function') return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        swiper.updateAutoHeight(0);
        this.getProgressBarHeight();
      });
    });
  }

  /**
   * Hover prefetch: collection (`data-action`) or filter (`data-name` / `data-value`).
   * Declarative: on:pointerenter="product-finder/prefetchFilter"
   * @param {Event} event
   */
  prefetchFilter(event) {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    if (!isDesktopBreakpoint() || Shopify.designMode || this.#isMultipleSelection) return;

    const fetchUrl = this.#buildStepFetchUrl(el);
    if (!fetchUrl) return;

    this.#clearHoverTimeout();
    this.#hoverTimeout = setTimeout(() => this.#prefetchFilters(fetchUrl), ProductFinderComponent.HOVER_DELAY);
  }

  /**
   * Cancel a pending hover prefetch when the pointer leaves the control.
   * Declarative: on:pointerleave="product-finder/handleHoverOut"
   */
  handleHoverOut() {
    this.#clearHoverTimeout();
  }

  #clearHoverTimeout() {
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
  }

  get #isMultipleSelection() {
    return this.dataset.filterMultipleSelection === 'true';
  }

  get #emailCaptureEnabled() {
    return this.dataset.emailCapture === 'true';
  }

  /**
   * Declarative: on:click="product-finder/selectFilter"
   * @param {Event} event
   */
  selectFilter = async (event) => {
    const btn = event.target;
    if (!(btn instanceof HTMLElement)) return;

    // Multiple selection: toggle selected state, defer advancing to nextStep
    if (this.#isMultipleSelection && btn.dataset.name) {
      btn.classList.toggle('is-selected');
      return;
    }

    const activeIndexAtClick = this.querySelector('carousel-component')?.swiper?.activeIndex;
    const url = this.#urlFromStepInputs(btn);
    if (!url) return;

    await this.#fetchAndAdvance(url, btn, {
      activeIndex: activeIndexAtClick,
      paramName: btn.dataset.name ?? null,
      answer: this.#captureAnswer(btn),
    });
  };

  /**
   * Advance using all selected buttons in the current slide (multiple-selection mode).
   * Declarative: on:click="product-finder/nextStep"
   * @param {Event} event
   */
  nextStep = async (event) => {
    if (!this.#isMultipleSelection) return;

    const swiper = this.querySelector('carousel-component')?.swiper;
    if (!swiper) return;

    const carousel = this.querySelector('carousel-component');
    const activeSlide = swiper.slides[swiper.activeIndex];
    const selected = [...(activeSlide?.querySelectorAll?.('.product-finder-step__button.is-selected[data-name]') ?? [])];

    // No selection: skip AJAX, walk slides to skip empties (same as AJAX logic)
    if (!selected.length) {
      const activeIndexAtClick = swiper.activeIndex;
      let offset = 1;
      for (let i = activeIndexAtClick + 1; i < swiper.slides.length; i++) {
        const filterEl = swiper.slides[i]?.querySelector('.product-finder-step__filter[data-filter]');
        if (!filterEl || filterEl.innerHTML.trim()) break;
        offset++;
      }
      this.#dataFilterSelections.push({ activeIndex: activeIndexAtClick });
      if (this.#emailCaptureEnabled) this.#answerLog.push(null);
      this.#advanceCarousel(carousel, activeIndexAtClick, offset);
      return;
    }

    const url = new URL(this.dataset.baseUrl || window.location.href, window.location.origin);
    /** @type {Set<string>} */
    const paramNames = new Set();
    selected.forEach((btn) => {
      url.searchParams.append(btn.dataset.name, btn.dataset.value);
      paramNames.add(btn.dataset.name);
    });

    const triggerEl = event.target instanceof HTMLElement ? event.target : null;
    await this.#fetchAndAdvance(url, triggerEl, {
      activeIndex: swiper.activeIndex,
      paramNames: [...paramNames],
      answer: this.#captureAnswer(null, selected),
    });
  };

  /**
   * Fetch step HTML for `url`, set base URL, record `selectionRecord`, then merge+advance.
   * Adds/removes `btn--loading` on `triggerEl` while in flight.
   * @param {URL} url
   * @param {HTMLElement | null} triggerEl
   * @param {{ activeIndex: number, paramName?: string | null, paramNames?: string[] }} selectionRecord
   */
  #fetchAndAdvance = async (url, triggerEl, selectionRecord) => {
    triggerEl?.classList.add('btn--loading');
    this.#isClickFetching = true;
    try {
      this.#applyBaseUrl(url.toString());
      const fetchUrl = this.#toFetchUrl(new URL(url.toString()));

      const newHTML = await this.#cache.get(fetchUrl);
      if (newHTML && typeof selectionRecord.activeIndex === 'number') {
        this.#dataFilterSelections.push(selectionRecord);
        if (this.#emailCaptureEnabled) this.#answerLog.push(selectionRecord.answer ?? null);
        this.#mergeFiltersIntoStep(newHTML, triggerEl);
      }
    } finally {
      this.#isClickFetching = false;
      triggerEl?.classList.remove('btn--loading');
    }

    // Chain: if the step we landed on has a single option, apply it and continue.
    const activeIndex = this.querySelector('carousel-component')?.swiper?.activeIndex;
    if (typeof activeIndex === 'number') {
      await this.#autoSkipIfSingleOption(activeIndex);
    }
  };

  /**
   * Back: pop last entry from `#dataFilterSelections`; if it has `paramName`, remove from `data-base-url`;
   * clear that slide’s filter HTML; `slideTo` stored `activeIndex`.
   */
  prevStep = (event) => {
    const carousel = this.querySelector('carousel-component');
    if (!carousel || typeof carousel.slideTo !== 'function') return;

    const lastSelection = this.#dataFilterSelections.pop();
    if (!lastSelection) return;
    this.#answerLog.pop();

    if (lastSelection.paramNames) {
      lastSelection.paramNames.forEach((n) => this.#deleteParamFromBaseUrl(n));
    } else if (lastSelection.paramName) {
      this.#deleteParamFromBaseUrl(lastSelection.paramName);
    }
    const targetIndex = Math.max(0, lastSelection.activeIndex);
    this.#clearSelectedButtons(carousel.swiper?.slides?.[carousel.swiper?.activeIndex]);
    carousel.slideTo(targetIndex);
    this.#updateProgressBarForSlideIndex(targetIndex);
  };

  retakeQuiz = () => {
    this.#dataFilterSelections = [];
    this.#answerLog = [];
    this.#cache.clear();
    this.#cache.abort();
    this.#applyBaseUrl(this.#initialBaseUrl);
    this.#clearSelectedButtons();
    this.querySelector('carousel-component')?.swiper?.slideTo(0);
    this.#updateProgressBarForSlideIndex(0);
  };

  // ---------------------------------------------------------------------------
  // Email capture (only active when `data-email-capture="true"`)
  // ---------------------------------------------------------------------------

  /**
   * Capture the current step's question + chosen label(s) for the subscription note.
   * @param {HTMLElement | null} [triggerEl] - Single-select clicked button
   * @param {HTMLElement[]} [selected] - Multi-select chosen buttons
   * @returns {{ question: string, answer: string } | null}
   */
  #captureAnswer(triggerEl = null, selected = null) {
    if (!this.#emailCaptureEnabled) return null;
    const swiper = this.querySelector('carousel-component')?.swiper;
    const activeSlide = swiper?.slides?.[swiper.activeIndex];
    const question = activeSlide?.querySelector('.product-finder-step__heading')?.textContent?.trim() ?? '';
    const answer = selected?.length
      ? selected.map((btn) => this.#buttonLabel(btn)).filter(Boolean).join(', ')
      : this.#buttonLabel(triggerEl);
    if (!answer) return null;
    return { question, answer };
  }

  /**
   * @param {HTMLElement | null} btn
   * @returns {string}
   */
  #buttonLabel(btn) {
    const el = btn?.closest?.('.product-finder-step__button') ?? btn;
    // The button carries its label in data-label (textContent would miss it on swatch filters,
    // where the first child is the empty swatch box).
    return (el?.dataset?.label ?? el?.textContent ?? '').trim();
  }

  /**
   * Fill the customer note and stash the result URL right before the native customer-form
   * submit reloads the page. Never preventDefault — Shopify's CAPTCHA + submit run as normal.
   * @param {Event} event
   */
  #onSubscribeSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('product-finder__email-form')) return;

    const noteInput = form.querySelector('.product-finder__email-note');
    if (noteInput instanceof HTMLInputElement) noteInput.value = this.#buildSubscriptionNote();

    this.#persistResultForReopen();
  };

  /** @returns {string} sessionStorage key scoped to this finder's signup form id. */
  #reopenStorageKey() {
    const formId = this.dataset.signupFormId;
    return formId ? `product-finder:${formId}` : '';
  }

  /** Stash the result URL + scroll position so the finder can reopen + restore them after reload. */
  #persistResultForReopen() {
    const key = this.#reopenStorageKey();
    if (!key) return;
    try {
      sessionStorage.setItem(key, JSON.stringify({ resultUrl: this.dataset.baseUrl ?? '', scrollY: window.scrollY }));
    } catch {
      /* storage unavailable — the finder just won't auto-reopen */
    }
  }

  /**
   * After a customer-form reload (`?customer_posted=true#<form id>`), reopen the dialog and
   * restore the results slide from the stashed URL.
   */
  async #maybeReopenAfterSubscribe() {
    const formId = this.dataset.signupFormId;
    if (!formId) return;
    if (new URLSearchParams(window.location.search).get('customer_posted') !== 'true') return;
    if (!window.location.hash.includes(formId)) return;

    const key = this.#reopenStorageKey();
    const stored = key ? sessionStorage.getItem(key) : null;
    if (key) sessionStorage.removeItem(key);
    if (!stored) return;

    let resultUrl = '';
    let scrollY = 0;
    try {
      const data = JSON.parse(stored);
      resultUrl = data.resultUrl ?? '';
      scrollY = Number(data.scrollY) || 0;
    } catch {
      resultUrl = '';
    }
    if (!resultUrl) return;

    // Stop a manual refresh from re-triggering the reopen.
    if (window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      url.hash = '';
      window.history.replaceState(null, '', url.toString());
    }

    const openButton = this.querySelector('.popup-link__button');
    if (!(openButton instanceof HTMLElement)) return;

    // Hidden until on results (a closed dialog is display:none → Swiper counts 0 slides, so open
    // first, then jump). #restoreResults reveals it.
    this.classList.add('product-finder--restoring');
    this.#stampDialogContent();
    await this.#waitForSwiper();
    openButton.click();
    await this.#restoreResults(resultUrl);

    if (scrollY) setTimeout(() => window.scrollTo(0, scrollY), 200);
  }

  /** Resolve with the carousel swiper once `<carousel-component>` is defined, or null after 2s. */
  async #waitForSwiper() {
    const defined = window.customElements?.whenDefined?.('carousel-component') ?? Promise.resolve();
    await Promise.race([defined, new Promise((resolve) => setTimeout(resolve, 2000))]);
    return this.querySelector('carousel-component')?.swiper || null;
  }

  /**
   * Re-fetch the filtered products for `resultUrl`, inject them into the results slide, and jump to it.
   * @param {string} resultUrl
   */
  async #restoreResults(resultUrl) {
    this.#applyBaseUrl(resultUrl);
    try {
      const carousel = this.querySelector('carousel-component');
      const swiper = carousel?.swiper;
      if (!carousel || !swiper || typeof carousel.slideTo !== 'function') return;

      // Recount slides — they were 0 while the dialog was display:none.
      swiper.update();

      const resultsIndex = [...swiper.slides].findIndex((slide) => slide.querySelector('.product-finder-step--results'));
      if (resultsIndex < 0) return;

      swiper.slideTo(resultsIndex, 0);
      this.#updateProgressBarForSlideIndex(resultsIndex);

      const newHTML = await this.#cache.get(this.#toFetchUrl(new URL(resultUrl, window.location.origin)));
      if (!newHTML) return;

      this.#injectResultProducts(newHTML);
    } finally {
      // Reveal the slides now that we're on results (or bail-out — never leave them hidden).
      this.classList.remove('product-finder--restoring');
    }
  }

  /** @param {Document} newHTML */
  #injectResultProducts(newHTML) {
    const productsElement = this.querySelector('.product-finder-step__products');
    const newProductsElement = newHTML.querySelector('.product-finder-step__products');
    const newNoMatchesElement = newHTML.querySelector('.product-finder-step__no-matches');
    const noMatchesElement = this.querySelector('.product-finder-step__no-matches');
    const matchesElement = this.querySelector('.product-finder-step__matches');
    const hasMatches = !!newProductsElement;
    matchesElement?.classList.toggle('hidden', !hasMatches);
    noMatchesElement?.classList.toggle('hidden', hasMatches);
    if (hasMatches && productsElement) {
      productsElement.innerHTML = newProductsElement.innerHTML;
    } else if (newNoMatchesElement && !noMatchesElement) {
      matchesElement?.after(newNoMatchesElement);
    }
  }

  /**
   * Add the result product in the quiz-selected size.
   * Declarative: on:click="product-finder/addSizeToCart"
   * @param {Event} event
   */
  addSizeToCart = async (event) => {
    const btn =
      event.target instanceof HTMLElement
        ? event.target.closest('.product-finder-step__add-size')
        : null;
    if (!(btn instanceof HTMLElement)) return;

    const variantId = btn.dataset.variantId;
    if (!variantId || btn.classList.contains('btn--loading') || btn.disabled) return;

    btn.classList.add('btn--loading');
    btn.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const announcement = announceCartLinesUpdate(this, {
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        }),
      });
      const data = await response.json();

      if (data.status) {
        const reason = data.description || data.message || 'Cart Error';
        announcement.reject(new Error(reason));
        announceCartError(this, {
          error: reason,
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        });
        document.dispatchEvent(new CartErrorEvent('product-finder', reason));
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'product-finder',
          productId: btn.dataset.productId,
          variantId: String(variantId),
          sections: data.sections,
        })
      );
      settleWithFetchedCart(announcement, {
        source: 'product-finder',
        productId: btn.dataset.productId,
        sections: data.sections,
      });

      const label = btn.querySelector('.btn__text');
      const original = label?.textContent?.trim() ?? '';
      const addedLabel = btn.dataset.addedLabel?.trim();
      if (label && addedLabel) label.textContent = addedLabel;
      btn.classList.add('is-added');
      window.setTimeout(() => {
        if (label && original) label.textContent = original;
        btn.classList.remove('is-added');
      }, 1800);
    } catch (error) {
      announcement.reject(error);
      announceCartError(this, {
        error: error?.message ?? String(error),
        code: 'SERVICE_UNAVAILABLE',
      });
      document.dispatchEvent(new CartErrorEvent('product-finder', String(error)));
    } finally {
      btn.classList.remove('btn--loading');
      btn.removeAttribute('aria-busy');
    }
  };

  /**
   * Skip subscription and advance to the results slide.
   * Declarative: on:click="product-finder/skipEmailCapture"
   */
  skipEmailCapture = () => {
    this.#advanceToResults();
  };

  /** Slide from the email slide to the results slide (next slide) and sync the progress bar. */
  #advanceToResults() {
    const carousel = this.querySelector('carousel-component');
    const swiper = carousel?.swiper;
    if (!carousel || !swiper || typeof carousel.slideTo !== 'function') return;
    const nextIndex = swiper.activeIndex + 1;
    carousel.slideTo(nextIndex);
    this.#updateProgressBarForSlideIndex(nextIndex);
  }

  /**
   * Build the customer note: title, answers (question + chosen label), and top 2 result
   * product titles.
   * @returns {string}
   */
  #buildSubscriptionNote() {
    const lines = [];
    const title = this.dataset.noteTitle?.trim();
    if (title) lines.push(title);

    const answers = this.#answerLog.filter((entry) => entry && entry.answer);
    if (answers.length) {
      lines.push(this.dataset.noteAnswersLabel ?? '');
      answers.forEach(({ question, answer }) => {
        lines.push(`• ${question ? `${question}: ` : ''}${answer}`);
      });
    }

    const productTitles = [...this.querySelectorAll('.product-finder-step__products .product-card__title')]
      .slice(0, 2)
      .map((el) => el.textContent.trim())
      .filter(Boolean);
    if (productTitles.length) {
      lines.push(this.dataset.noteResultsLabel ?? '');
      productTitles.forEach((productTitle) => lines.push(`• ${productTitle}`));
    }

    return lines.join('\n');
  }

  /**
   * Remove `is-selected` from buttons. Scoped to `root` if provided, else entire component.
   * @param {Element} [root]
   */
  #clearSelectedButtons(root = this) {
    root?.querySelectorAll?.('.product-finder-step__button.is-selected')
      .forEach((btn) => btn.classList.remove('is-selected'));
  }

  /**
   * Remove a facet param from `data-base-url`.
   * @param {string} paramName
   */
  #deleteParamFromBaseUrl(paramName) {
    if (!paramName || !this.dataset.baseUrl) return;
    const url = new URL(this.dataset.baseUrl, window.location.origin);
    url.searchParams.delete(paramName);
    this.#applyBaseUrl(url.toString());
  }

  /**
   * Build URL from control `dataset` (`data-action` or `data-name` / `data-value` + `data-base-url`).
   * @param {HTMLElement} el
   * @returns {URL | null}
   */
  #urlFromStepInputs(el) {
    const collectionUrl = el.dataset.action;

    if (collectionUrl) {
      return new URL(collectionUrl, window.location.origin);
    }
    const paramName = el.dataset.name;
    const paramValue = el.dataset.value;
    if (!paramName || !paramValue || !this.dataset.baseUrl) return null;
    const url = new URL(this.dataset.baseUrl, window.location.origin);
    url.searchParams.set(paramName, paramValue);
    return url;
  }

  /** Sets `data-base-url` and keeps the “view more results” link in sync. */
  #applyBaseUrl(href) {
    this.dataset.baseUrl = href;
    this.querySelector('.more-results-button')?.setAttribute('href', href);
  }

  /**
   * Append `section_id`, sort params, and return the fetch URL string.
   * @param {URL} url
   * @returns {string}
   */
  #toFetchUrl(url) {
    url.searchParams.set('section_id', ProductFinderComponent.NORMALIZED_STEP_SECTION_ID);
    url.searchParams.sort();
    return url.toString();
  }

  /**
   * Section fetch URL from the clicked control (via `#urlFromStepInputs` + `section_id`).
   * @param {HTMLElement} el
   * @returns {string}
   */
  #buildStepFetchUrl(el) {
    const url = this.#urlFromStepInputs(el);
    return url ? this.#toFetchUrl(url) : '';
  }

  /**
   * @param {string} fetchUrl
   */
  #prefetchFilters(fetchUrl) {
    // Don't let a trailing hover prefetch abort an in-flight click fetch.
    if (this.#isClickFetching) return;
    this.#cache.prefetch(fetchUrl);
  }

  /**
   * Fetcher for the prefetch cache: fetch the step section and parse it into a
   * Document. Aborts (and non-ok responses) are surfaced as `null`.
   * @param {string} fetchUrl
   * @param {AbortSignal} [signal] - Abort signal from the cache
   * @returns {Promise<Document | null>}
   */
  async #fetchSection(fetchUrl, signal) {
    try {
      const response = await fetch(fetchUrl, { signal });
      if (!response.ok) return null;
      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch {
      return null;
    }
  }

  /**
   * Merge fetched step HTML into the next destination slide only.
   * Skips empty filters (offset++) until a match, then stops — later slides stay empty so
   * fade-stacked content cannot flash under the active question on hover.
   * @param {Document} newHTML — parsed section HTML
   */
  #mergeFiltersIntoStep(newHTML, currentFilterButton) {
    if (!newHTML.querySelector('.product-finder-step__filter[data-filter]')) return;

    const carousel = this.querySelector('carousel-component');
    const swiper = carousel?.swiper;
    if (!carousel || !swiper || typeof carousel.slideTo !== 'function') return;

    const { activeIndex, slides } = swiper;

    let carouselSlideOffset = 1;
    let firstMatchFound = false;

    for (let i = activeIndex + 1; i < slides.length; i++) {
      const filterElement = slides[i]?.querySelector?.('.product-finder-step__filter[data-filter]');
      if (!filterElement) continue;

      const filterKey = filterElement.getAttribute('data-filter');

      const matched = this.#findFilterFragmentForKey(newHTML, filterKey);
      if (!matched) {
        filterElement.innerHTML = '';
        if (!firstMatchFound) carouselSlideOffset++;
        continue;
      }
      filterElement.innerHTML = matched.innerHTML;
      firstMatchFound = true;

      // Keep upcoming steps empty until we actually land on them (avoids brand grid
      // peeking through the type step while the mouse moves over the fade stack).
      for (let j = i + 1; j < slides.length; j++) {
        const laterFilter = slides[j]?.querySelector?.('.product-finder-step__filter[data-filter]');
        if (laterFilter) laterFilter.innerHTML = '';
      }
      break;
    }

    const productsElement = this.querySelector('.product-finder-step__products');
    const newProductsElement = newHTML.querySelector('.product-finder-step__products');
    const newNoMatchesElement = newHTML.querySelector('.product-finder-step__no-matches');
    const noMatchesElement = this.querySelector('.product-finder-step__no-matches');
    const matchesElement = this.querySelector('.product-finder-step__matches');

    const hasMatches = !!newProductsElement;
    matchesElement?.classList.toggle('hidden', !hasMatches);
    noMatchesElement?.classList.toggle('hidden', hasMatches);

    if (hasMatches && productsElement) {
      productsElement.innerHTML = newProductsElement.innerHTML;
    } else if (newNoMatchesElement && !noMatchesElement) {
      matchesElement?.after(newNoMatchesElement);
    }
    this.#advanceCarousel(carousel, swiper.activeIndex, carouselSlideOffset);
  }

  /**
   * @param {*} carousel
   * @param {number} fromIndex
   * @param {number} offset
   */
  #advanceCarousel(carousel, fromIndex, offset) {
    const nextIndex = fromIndex + offset;
    carousel.slideTo(nextIndex);
    this.#updateProgressBarForSlideIndex(nextIndex);
  }

  /**
   * If the landed step has exactly one option, apply it and continue — no dead-end taps.
   * @param {number} slideIndex
   */
  async #autoSkipIfSingleOption(slideIndex) {
    if (this.#isClickFetching) return;

    const carousel = this.querySelector('carousel-component');
    const swiper = carousel?.swiper;
    if (!carousel || !swiper || swiper.activeIndex !== slideIndex) return;

    const slide = swiper.slides[slideIndex];
    if (!slide?.querySelector?.('.product-finder-step--facets')) return;
    // Results / email are not facet steps with choices to auto-apply.
    if (slide.querySelector('.product-finder-step--results, .product-finder-step--email')) return;

    const buttons = [...(slide.querySelectorAll('.product-finder-step__button') ?? [])].filter(
      (btn) => btn instanceof HTMLElement && (btn.dataset.name || btn.dataset.action)
    );
    if (buttons.length !== 1) return;

    const btn = buttons[0];
    const url = this.#urlFromStepInputs(btn);
    if (!url) return;

    await this.#fetchAndAdvance(url, btn, {
      activeIndex: slideIndex,
      paramName: btn.dataset.name ?? null,
      answer: this.#captureAnswer(btn),
    });
  }

  /**
   * Updates the single shared `progress-bar` for the Swiper slide at `slideIndex`, then reinit animation.
   * @param {number} slideIndex
   */
  #updateProgressBarForSlideIndex(slideIndex) {
    const progressBar = this.querySelector('progress-bar');
    if (!progressBar) return;

    const carousel = this.querySelector('carousel-component');
    const swiper = carousel?.swiper;
    const slide = swiper?.slides?.[slideIndex];
    const stepRoot = slide?.querySelector?.('.product-finder-step--facets');
    const steps = this.querySelectorAll('.product-finder-step--facets');
    const i = stepRoot ? [...steps].indexOf(stepRoot) : -1;
    if (i < 0 || !steps.length) return;

    const value = Math.round((i / steps.length) * 100);
    const minValue = value === 0 ? 5 : value;
    progressBar.setAttribute('data-value', String(minValue));

    const progressBarValue = this.querySelector('.product-finder__progress-bar-value');
    if (progressBarValue) {
      progressBarValue.textContent = `${minValue}%`;
      const separator = this.querySelector('.product-finder__progress-bar-separator');
      if (value === 0) {
        progressBarValue.classList.add('visually-hidden');
        separator?.classList.add('visually-hidden');
      } else {
        progressBarValue.classList.remove('visually-hidden');
        separator?.classList.remove('visually-hidden');
      }
    }

    progressBar.dispatchEvent(new CustomEvent(ThemeEvents.progressBarUpdate, { bubbles: true }));
  }

  /**
   * Gets the height of the progress bar and sets the CSS custom property.
   */
  getProgressBarHeight() {
    const bar = this.querySelector('.product-finder__progress-bar');
    if (!bar) return;

    const text = bar.querySelector('.product-finder__progress-bar-text');
    if (!text) return;

    const height = Math.round(bar.offsetHeight);
    if (height <= 0) {
      this.style.removeProperty('--finder-progress-bar-height');
      return;
    }

    this.style.setProperty('--finder-progress-bar-height', `${height}px`);
  }

  /**
   * First fragment in the section response whose `data-filter` equals `filterKey` (case-insensitive).
   * @param {ParentNode} root
   * @param {string} filterKey
   * @returns {Element | null}
   */
  #findFilterFragmentForKey(root, filterKey) {
    const filterKeyUpper = filterKey.toUpperCase();
    for (const el of root.querySelectorAll('.product-finder-step__filter[data-filter]')) {
      if ((el.getAttribute('data-filter') ?? '').toUpperCase() === filterKeyUpper && el.innerHTML.trim()) return el;
    }
    return null;
  }
}

if (!customElements.get('product-finder')) {
  customElements.define('product-finder', ProductFinderComponent);
}
